import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";

import ClientLayout from "./layouts/ClientLayout";
import LoginPage from "./pages/loginPage/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import AppointmentsPage from "./pages/AppointmentsPage/AppointmentsPage";
import AppointmentDetailsPage from "./pages/appointmentDetailsPage/AppointmentDetailsPage";

import VerifyClientEmailPage from "./pages/verifyClientEmailPage/VerifyClientEmailPage";

import { useAuthContext } from "./hooks/useAuthContext";
import { ApiConnectionString } from "./constants/ApiConntection";

import "./App.css";
import BookAppointmentPage from "./pages/bookAppointmentPage/BookAppointmentPage";
import ClientSettingsPage from "./pages/clientSettingsPage/ClientSettingsPage";
import ResetPasswordPage from "./pages/resetPasswordPage/ResetPasswordPage";

export default function App() {
  const { user, dispatch } = useAuthContext();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const restoreUser = async () => {
      const storedUser = localStorage.getItem("clientUser");

      if (!storedUser) {
        return;
      }

      let existingUser;

      try {
        existingUser = JSON.parse(storedUser);
      } catch {
        localStorage.removeItem("clientUser");
        dispatch({ type: "LOGOUT", payload: {} });
        return;
      }

      if (!existingUser?.token) {
        localStorage.removeItem("clientUser");
        dispatch({ type: "LOGOUT", payload: {} });
        return;
      }

      try {
        await axios.get(`${ApiConnectionString}/api/client/checkStatus`, {
          headers: {
            Authorization: `Bearer ${existingUser.token}`,
          },
        });

        dispatch({
          type: "WELCOMEBACK",
          payload: existingUser,
        });
      } catch (error) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem("clientUser");
          dispatch({ type: "LOGOUT", payload: {} });
        }
      }
    };

    restoreUser();
  }, [dispatch]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.token) {
        setProfile(null);
        return;
      }

      try {
        const response = await axios.get(
          `${ApiConnectionString}/api/client/myprofile`,
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          },
        );

        setProfile(response.data);
      } catch (error) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem("clientUser");
          setProfile(null);
          dispatch({ type: "LOGOUT", payload: {} });
        }
      }
    };

    fetchProfile();
  }, [user?.token, dispatch]);

  const handleLogin = (clientUser) => {
    localStorage.setItem("clientUser", JSON.stringify(clientUser));

    dispatch({
      type: "LOGIN",
      payload: clientUser,
    });
  };

  const getLoginRedirect = () => {
    const storedUser = localStorage.getItem("clientUser");

    if (!storedUser) {
      return "/";
    }

    try {
      const existingUser = JSON.parse(storedUser);

      if (existingUser?.slug) {
        return `/${existingUser.slug}/login`;
      }
    } catch {
      localStorage.removeItem("clientUser");
    }

    return "/";
  };

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/resetpassword/:token" element={<ResetPasswordPage />} />
        <Route path="/:slug/verify-email" element={<VerifyClientEmailPage />} />

        <Route
          path="/:slug/login"
          element={
            user ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <LoginPage onLogin={handleLogin} />
            )
          }
        />

        <Route
          element={
            user ? (
              <ClientLayout profile={profile} />
            ) : (
              <Navigate to={getLoginRedirect()} replace />
            )
          }
        >
          <Route path="/bookAppointment" element={<BookAppointmentPage />} />
          <Route
            path="/dashboard"
            element={<DashboardPage profile={profile} />}
          />

          <Route
            path="/appointments"
            element={<AppointmentsPage profile={profile} />}
          />

          <Route
            path="/appointments/:id"
            element={<AppointmentDetailsPage />}
          />
          <Route path="/settings" element={<ClientSettingsPage />} />
        </Route>

        <Route
          path="/"
          element={
            user ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <div className="login-page">
                <div className="login-card">
                  <section className="login-form">
                    <h2>Client portal</h2>

                    <p>
                      Please use the login link given to you by your therapist.
                    </p>
                  </section>
                </div>
              </div>
            )
          }
        />

        <Route
          path="*"
          element={
            user ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to={getLoginRedirect()} replace />
            )
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
